import { useCalendarEvents } from '@/composables/useCalendarEvents'
import { useExternalEventIndicators } from '@/composables/useExternalEventIndicators'
import { useCalendarMainView } from '@/composables/useCalendarMainView'
import { useCalendarPageState } from '@/composables/useCalendarPageState'
import { useCalendarSidebar } from '@/composables/useCalendarSidebar'
import { useConnectedCalendars } from '@/composables/useConnectedCalendars'
import { calendarSupportsParticipantInvites } from '@/composables/useProviderCapabilities'
import {
  addCalendarDays,
  buildMiniMonthGrid,
  startOfDay,
  startOfWeekMonday
} from '@/utils/calendar/date'
import { CALENDAR_EVENT_TYPE_ORDER, resolveCalendarEventType } from '@/utils/calendar/event-types'
import {
  CalendarEventTypeEnum,
  type CalendarEventTypeEnum as CalendarEventType
} from '@/types/generated'
import { computed, ref, watch, type Ref } from 'vue'

const FIXED_SIDEBAR_FILTER_TYPES: CalendarEventType[] = [
  CalendarEventTypeEnum.TERMIN,
  CalendarEventTypeEnum.GERICHTSTERMIN,
  CalendarEventTypeEnum.PRIVAT
]

export interface CalendarPageLabels {
  viewDay: string
  viewWeek: string
  viewMonth: string
  viewYear: string
  prevDay: string
  prevWeek: string
  prevMonth: string
  prevYear: string
  nextDay: string
  nextWeek: string
  nextMonth: string
  nextYear: string
}

export type CalendarPageFilterLabels = Record<CalendarEventType, string>

/**
 * Orchestrates calendar page data: connected calendars, focused filters, and grid events.
 */
export function useCalendarPage(
  toolbarLabels: Ref<CalendarPageLabels>,
  filterLabels: Ref<CalendarPageFilterLabels>,
  untitledAppointmentLabel: Ref<string>
) {
  const endOfDay = (date: Date): Date => {
    const value = startOfDay(date)
    value.setDate(value.getDate() + 1)
    value.setMilliseconds(value.getMilliseconds() - 1)
    return value
  }

  const layout = useCalendarPageState()
  const calendars = useConnectedCalendars()
  const mainView = useCalendarMainView(layout.selectedDate, toolbarLabels)
  const activeFilterTypes = ref<Set<CalendarEventType>>(new Set())
  const visibleRange = computed(() => {
    const selectedDate = layout.selectedDate.value

    if (mainView.activeView.value === 'week') {
      const start = startOfWeekMonday(selectedDate)
      return {
        start,
        end: endOfDay(addCalendarDays(start, 6))
      }
    }

    if (mainView.activeView.value === 'month') {
      const start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
      return {
        start: startOfDay(start),
        end: endOfDay(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0))
      }
    }

    if (mainView.activeView.value === 'year') {
      return {
        start: startOfDay(new Date(selectedDate.getFullYear(), 0, 1)),
        end: endOfDay(new Date(selectedDate.getFullYear(), 11, 31))
      }
    }

    return {
      start: startOfDay(selectedDate),
      end: endOfDay(selectedDate)
    }
  })
  const participantInvitesSupported = computed(() => {
    const calendarUuid = calendars.selectedCalendarUuid.value
    const calendar = calendars.visibleCalendars.value.find((item) => item.uuid === calendarUuid)
    return calendarSupportsParticipantInvites(calendar)
  })

  const calendarEvents = useCalendarEvents({
    selectedCalendarUuid: calendars.selectedCalendarUuid,
    visibleRangeStart: computed(() => visibleRange.value.start.toISOString()),
    visibleRangeEnd: computed(() => visibleRange.value.end.toISOString()),
    activeFilterTypes,
    untitledLabel: untitledAppointmentLabel,
    canEditCalendar: (calendarUuid: string) =>
      Boolean(
        calendars.visibleCalendars.value.find(
          (calendar) => calendar.uuid === calendarUuid && !calendar.calendar_readonly
        )
      ),
    participantInvitesSupported
  })

  const availableTypesByCalendar = computed<Record<string, CalendarEventType[]>>(() => {
    const presentTypes: Record<string, Set<CalendarEventType>> = {}

    for (const event of calendarEvents.events.value) {
      if (!presentTypes[event.calendar]) {
        presentTypes[event.calendar] = new Set()
      }

      presentTypes[event.calendar]!.add(resolveCalendarEventType(event))
    }

    const orderedTypes: Record<string, CalendarEventTypeEnum[]> = {}

    for (const calendar of calendars.visibleCalendars.value) {
      if (!calendar.uuid) {
        continue
      }

      const types = presentTypes[calendar.uuid]
      const allowedTypes = new Set<CalendarEventType>(FIXED_SIDEBAR_FILTER_TYPES)

      for (const type of types ?? []) {
        allowedTypes.add(type)
      }

      orderedTypes[calendar.uuid] = CALENDAR_EVENT_TYPE_ORDER.filter((type) =>
        allowedTypes.has(type)
      )
    }

    return orderedTypes
  })

  const sidebar = useCalendarSidebar(
    calendars.visibleCalendars,
    calendars.selectedCalendarUuid,
    availableTypesByCalendar,
    filterLabels
  )

  watch(
    sidebar.activeFilterTypes,
    (types) => {
      activeFilterTypes.value = new Set(types)
    },
    { immediate: true }
  )

  const indicators = useExternalEventIndicators(calendarEvents.eventDates)

  watch(calendars.selectedCalendarUuid, (uuid) => {
    if (uuid) {
      sidebar.ensureFiltersExpanded(uuid)
    }
  })

  watch(
    () => mainView.activeView.value,
    (view) => {
      layout.highlightSelectedWeek.value = view === 'week'
    },
    { immediate: true }
  )

  const miniMonthCells = computed(() =>
    buildMiniMonthGrid({
      anchor: layout.miniMonthAnchor.value,
      selected: layout.selectedDate.value,
      today: layout.getToday(),
      highlightWeek: layout.highlightSelectedWeek.value,
      datesWithEvents: indicators.datesWithEvents.value
    })
  )

  const selectCalendar = (uuid: string): void => {
    calendars.selectCalendar(uuid)
    if (uuid) {
      sidebar.ensureFiltersExpanded(uuid)
    }
  }

  const onYearOpenDay = (): void => {
    mainView.setView('day')
  }

  return {
    ...layout,
    ...mainView,
    miniMonthCells,
    ...sidebar,
    ...calendarEvents,
    appointmentsIsLoading: calendarEvents.isLoading,
    appointmentsLoadError: calendarEvents.loadError,
    eventsByDate: calendarEvents.eventsByDate,
    ...calendars,
    selectCalendar,
    onYearOpenDay
  }
}
